/**
 * The control-side half of `./capabilities.ts` (DEC-01, bata-ui ADR 9022): render a decision
 * control only when this person may take the act, and otherwise a short "why not" in its place —
 * never a button that 403s.
 *
 * `CapabilityGate` renders its children when the verdict is `allowed` or `unknown` (the read is
 * loading, failed or not published: the platform still judges the write), and `WhyNot` when the
 * platform says no. The reason is the platform's, in words; the roles that grant the act are
 * named, so the person knows whom to ask — the answer to "who can do this" that DEC-01 wants
 * visible before profiling makes it matter.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';

import { codeWords, ROLE_LABEL_KEY } from '@/planning/words';

import {
  WHY_NOT_KEY, mayRender, useCapability,
  type CapabilityAction, type CapabilityKind, type CapabilityVerdict, type CapabilityWhere,
} from './capabilities';

type DeniedVerdict = Extract<CapabilityVerdict, { state: 'denied' }>;

/**
 * The roles that grant a denied act, in words (DEC-111, issue 203 finding 3): the realm's role ids
 * through `ROLE_LABEL_KEY`, the same map Profile and User management read. A role this build has no
 * words for is shown as its id — a wrong word is worse than the code.
 */
export function useRoleWords(roles: readonly string[]): readonly string[] {
  const { t } = useTranslation();
  return roles.map((role) => codeWords(t, ROLE_LABEL_KEY, role));
}

/** A denial's sentence: its catalogue key and arguments — the roles and markets as a list. */
export function useWhyNotSentence(verdict: DeniedVerdict): { readonly key: string; readonly values: Record<string, string> } {
  const { t, i18n } = useTranslation();
  const roleWords = useRoleWords(verdict.grantedToRoles);
  const list = (items: readonly string[]): string => (items.length === 0
    ? t('capability.whyNot.nobody')
    : new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(items));
  return {
    key: WHY_NOT_KEY[verdict.reason],
    values: {
      roles: list(roleWords),
      countries: list(verdict.countries),
      country: verdict.country ?? '',
      reason: verdict.word ?? '',
    },
  };
}

export function WhyNot({ verdict }: { readonly verdict: DeniedVerdict }): React.JSX.Element {
  const { t } = useTranslation();
  const sentence = useWhyNotSentence(verdict);
  return (
    <p className="text-caption text-ink-secondary flex items-start gap-2 max-w-prose" data-why-not={verdict.reason}>
      <Lock size={14} aria-hidden className="shrink-0" style={{ marginBlockStart: 2 }} />
      {/* The role ids stay one hover away, as on Profile: the words are what a planner reads, the
          ids are what an administrator grants. */}
      <span {...(verdict.grantedToRoles.length === 0 ? {} : { title: verdict.grantedToRoles.join(', ') })}>{t(sentence.key, sentence.values)}</span>
    </p>
  );
}

export interface CapabilityGateProps {
  readonly kind: CapabilityKind;
  readonly action: CapabilityAction;
  readonly where?: CapabilityWhere;
  readonly children: ReactNode;
}

/** The control, or why it is not offered. */
export function CapabilityGate({ kind, action, where, children }: CapabilityGateProps): React.JSX.Element {
  const verdict = useCapability(kind, action, where);
  if (verdict.state === 'denied') return <WhyNot verdict={verdict} />;
  return <>{children}</>;
}

export { mayRender };
